import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useCategoriesData,
  useGetBook,
  useGetBookCategories,
  useGetBookVersions,
  useToggleBookStatus,
  useUpdateBookSimulationEligibility,
} from "@/hooks/api";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  MoreVertical,
  Edit,
  PlayCircle,
  Copy,
  XCircle,
} from "lucide-react";
import { MATERIALITIES } from "@/data/materialsAndCategories";

interface Edition {
  id: string;
  name: string;
  isbn: string;
}

interface CategoryConfig {
  csat: string;
  withoutNote: boolean;
}

interface BookDetails {
  id: string;
  title: string;
  editions: Edition[];
  currentEditionId: string;
  isbn: string;
  sankhyaCode: string;
  materialidadeId: number;
  materialidade: string;
  primaryCategory: string;
  secondaryCategory: string;
  categoryConfigs: Record<string, CategoryConfig>;
  cost: number;
  contractType: "Determinado" | "Indeterminado" | "";
  contractExpiryDate: string;
  /** Situação do contrato (vigência), não confundir com status do livro */
  contractStatus: "Ativo" | "Expirado" | "";
  /** Livro ativo/inativo na plataforma (`GET` book.status) */
  bookStatus: "ATIVO" | "INATIVO";
  simulationStatus: "APTO" | "INAPTO";
  lastUpdate: string;
}

const baseMockBookDetails: Record<string, BookDetails> = {
  "1": {
    id: "1",
    title: "Sr. Lobo Bom",
    editions: [
      { id: "1-1", name: "Edição 1", isbn: "9780123456765" },
      { id: "1-2", name: "Edição 2", isbn: "9786592806019" },
    ],
    currentEditionId: "1-2",
    isbn: "9786592806019",
    sankhyaCode: "2687",
    materialidadeId: 1,
    materialidade: "Capa dura",
    primaryCategory: "A1",
    secondaryCategory: "B1",
    categoryConfigs: {
      "A1": { csat: "3.2", withoutNote: false },
      "B1": { csat: "3.5", withoutNote: false },
    },
    cost: 25,
    contractType: "Determinado",
    contractExpiryDate: "9/11/2025",
    contractStatus: "Ativo",
    bookStatus: "ATIVO",
    simulationStatus: "APTO",
    lastUpdate: "hoje às 21:35",
  },
  "2": {
    id: "2",
    title: "Onde Vivem os Monstros",
    editions: [{ id: "2-1", name: "Edição 1", isbn: "9786586021349" }],
    currentEditionId: "2-1",
    isbn: "9786586021349",
    sankhyaCode: "11759",
    materialidadeId: 2,
    materialidade: "Brochura",
    primaryCategory: "A2",
    secondaryCategory: "B2",
    categoryConfigs: {
      "A2": { csat: "2.8", withoutNote: false },
      "B2": { csat: "3.1", withoutNote: false },
    },
    cost: 18,
    contractType: "Indeterminado",
    contractExpiryDate: "10/11/2025",
    contractStatus: "Expirado",
    bookStatus: "ATIVO",
    simulationStatus: "INAPTO",
    lastUpdate: "22 de nov. 2024",
  },
  "3": {
    id: "3",
    title: "A Casa da Árvore com 13 Andares",
    editions: [{ id: "3-1", name: "Edição 1", isbn: "9786586021356" }],
    currentEditionId: "3-1",
    isbn: "9786586021356",
    sankhyaCode: "17626",
    materialidadeId: 1,
    materialidade: "Capa dura",
    primaryCategory: "B1",
    secondaryCategory: "A1, C",
    categoryConfigs: {
      "B1": { csat: "3.7", withoutNote: false },
      "A1": { csat: "3.3", withoutNote: false },
      "C": { csat: "", withoutNote: true },
    },
    cost: 32,
    contractType: "Determinado",
    contractExpiryDate: "15/06/2026",
    contractStatus: "Ativo",
    bookStatus: "ATIVO",
    simulationStatus: "APTO",
    lastUpdate: "03 de fev. 2026",
  },
  "4": {
    id: "4",
    title: "Marcelo, Marmelo, Martelo",
    editions: [{ id: "4-1", name: "Edição 1", isbn: "9786586021790" }],
    currentEditionId: "4-1",
    isbn: "9786586021790",
    sankhyaCode: "18751",
    materialidadeId: 3,
    materialidade: "Pano",
    primaryCategory: "B2",
    secondaryCategory: "A2",
    categoryConfigs: {
      "B2": { csat: "3.0", withoutNote: false },
      "A2": { csat: "2.9", withoutNote: false },
    },
    cost: 22,
    contractType: "Indeterminado",
    contractExpiryDate: "05/05/2025",
    contractStatus: "Expirado",
    bookStatus: "ATIVO",
    simulationStatus: "INAPTO",
    lastUpdate: "18 de dez. 2023",
  },
  "5": {
    id: "5",
    title: "Menina Bonita do Laço de Fita",
    editions: [{ id: "5-1", name: "Edição 1", isbn: "9788592808262" }],
    currentEditionId: "5-1",
    isbn: "9788592808262",
    sankhyaCode: "21746",
    materialidadeId: 2,
    materialidade: "Brochura",
    primaryCategory: "E",
    secondaryCategory: "B2",
    categoryConfigs: {
      "E": { csat: "2.5", withoutNote: false },
      "B2": { csat: "3.2", withoutNote: false },
    },
    cost: 15,
    contractType: "Determinado",
    contractExpiryDate: "20/12/2025",
    contractStatus: "Ativo",
    bookStatus: "ATIVO",
    simulationStatus: "INAPTO",
    lastUpdate: "29 de jun. 2024",
  },
  "6": {
    id: "6",
    title: "Chapeuzinho Amarelo",
    editions: [{ id: "6-1", name: "Edição 1", isbn: "9788532520005" }],
    currentEditionId: "6-1",
    isbn: "9788532520005",
    sankhyaCode: "12345",
    materialidadeId: 5,
    materialidade: "Cartonado sem interatividade",
    primaryCategory: "A1",
    secondaryCategory: "A2, C",
    categoryConfigs: {
      "A1": { csat: "3.6", withoutNote: false },
      "A2": { csat: "3.4", withoutNote: false },
      "C": { csat: "3.0", withoutNote: false },
    },
    cost: 28,
    contractType: "Determinado",
    contractExpiryDate: "10/03/2026",
    contractStatus: "Ativo",
    bookStatus: "ATIVO",
    simulationStatus: "APTO",
    lastUpdate: "15 de jul. 2024",
  },
  "7": {
    id: "7",
    title: "O Grúfalo",
    editions: [{ id: "7-1", name: "Edição 1", isbn: "9788535922508" }],
    currentEditionId: "7-1",
    isbn: "9788535922508",
    sankhyaCode: "67890",
    materialidadeId: 1,
    materialidade: "Capa dura",
    primaryCategory: "B1",
    secondaryCategory: "A1",
    categoryConfigs: {
      "B1": { csat: "3.8", withoutNote: false },
      "A1": { csat: "3.5", withoutNote: false },
    },
    cost: 30,
    contractType: "Indeterminado",
    contractExpiryDate: "25/08/2025",
    contractStatus: "Expirado",
    bookStatus: "ATIVO",
    simulationStatus: "INAPTO",
    lastUpdate: "10 de ago. 2024",
  },
  "8": {
    id: "8",
    title: "Cada um com seu jeito, cada jeito é de um",
    editions: [{ id: "8-1", name: "Edição 1", isbn: "9788573021072" }],
    currentEditionId: "8-1",
    isbn: "9788573021072",
    sankhyaCode: "54321",
    materialidadeId: 2,
    materialidade: "Brochura",
    primaryCategory: "C",
    secondaryCategory: "B1",
    categoryConfigs: {
      "C": { csat: "3.3", withoutNote: false },
      "B1": { csat: "2.7", withoutNote: false },
    },
    cost: 19,
    contractType: "Determinado",
    contractExpiryDate: "14/11/2025",
    contractStatus: "Ativo",
    bookStatus: "INATIVO",
    simulationStatus: "APTO",
    lastUpdate: "20 de set. 2024",
  },
};

function randomizeBookDetails(base: Record<string, BookDetails>): Record<string, BookDetails> {
  const result: Record<string, BookDetails> = {};
  Object.values(base).forEach((b) => {
    result[b.id] = { ...b, categoryConfigs: { ...b.categoryConfigs } };
  });
  return result;
}

const mockBookDetails: Record<string, BookDetails> = randomizeBookDetails(baseMockBookDetails);

function contractSituationFromApi(
  contractType: "determinate" | "indeterminate" | null,
  expiration: string | null,
): BookDetails["contractStatus"] {
  if (contractType !== "determinate" || !expiration?.trim()) {
    return "";
  }
  const parsed = new Date(
    expiration.length <= 10 ? `${expiration}T12:00:00` : expiration,
  );
  if (Number.isNaN(parsed.getTime())) return "";
  const end = new Date(parsed);
  end.setHours(12, 0, 0, 0);
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return end.getTime() >= today.getTime() ? "Ativo" : "Expirado";
}

function getBookStatusBadgeClass(status: BookDetails["bookStatus"]): string {
  if (status === "ATIVO") {
    return "bg-[#135045] text-[#88C0B2] font-semibold text-xs";
  }
  return "bg-[#E8EFF0] text-[#3B494A] font-semibold text-xs";
}

function getSimulationBadgeClass(status: BookDetails["simulationStatus"]): string {
  switch (status) {
    case "APTO":
      return "bg-[#135045] text-[#88C0B2] font-semibold text-xs";
    case "INAPTO":
      return "bg-[#E8EFF0] text-[#3B494A] font-semibold text-xs";
  }
}

const splitCats = (s: string) =>
  s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

export default function BookView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const bookId = Number(id);
  const { data, isLoading, isError } = useGetBook(bookId);
  const { data: versionsData } = useGetBookVersions(bookId);
  const { data: categoriesData } = useGetBookCategories(bookId);
  const { data: categoriesCatalog } = useCategoriesData();
  const toggleStatusMutation = useToggleBookStatus();
  const simulationEligibilityMutation = useUpdateBookSimulationEligibility();

  const apiBook = data?.data;
  const versionsRaw = versionsData?.data;
  const versions = Array.isArray(versionsRaw)
    ? versionsRaw
    : versionsRaw && typeof versionsRaw === "object"
      ? [versionsRaw]
      : [];
  const categoriesRaw = categoriesData?.data;
  const bookCategories = Array.isArray(categoriesRaw)
    ? categoriesRaw
    : categoriesRaw &&
        typeof categoriesRaw === "object" &&
        Array.isArray(categoriesRaw.categories)
      ? categoriesRaw.categories
      : [];

  const primaryCodes = bookCategories
    .filter((c) => c.is_main)
    .map((cat) =>
      categoriesCatalog?.find((c) => Number(c.id) === Number(cat.category_id))?.code
    )
    .filter(Boolean) as string[];

  const secondaryCodes = bookCategories
    .filter((c) => !c.is_main)
    .map((cat) =>
      categoriesCatalog?.find((c) => Number(c.id) === Number(cat.category_id))?.code
    )
    .filter(Boolean) as string[];

  const categoryConfigs = bookCategories.reduce<Record<string, CategoryConfig>>((acc, cat) => {
    const code = categoriesCatalog?.find((c) => Number(c.id) === Number(cat.category_id))?.code;
    if (!code) return acc;
    acc[code] = {
      csat: cat.csat == null ? "" : String(cat.csat),
      withoutNote: !!cat.is_new,
    };
    return acc;
  }, {});

  const mapSimulationStatus = (
    value: unknown,
  ): BookDetails["simulationStatus"] => {
    if (value === "apto" || value === true) return "APTO";
    if (value === "inapto" || value === false) return "INAPTO";
    if (value === "incompleto") return "INAPTO";
    return "INAPTO";
  };

  const primaryEdition = versions[0];
  const contractType =
    apiBook?.contract_type === "determinate"
      ? "Determinado"
      : apiBook?.contract_type === "indeterminate"
        ? "Indeterminado"
        : "";
  const resolvedBook: BookDetails | null =
    apiBook && id
      ? {
          id: String(apiBook.id),
          title: apiBook.title ?? "",
          editions: versions.map((v, index) => ({
            id: String(v.id ?? `${apiBook.id}-${index + 1}`),
            name: `Edição ${index + 1}`,
            isbn: String(v.isbn ?? ""),
          })),
          currentEditionId: String(primaryEdition?.id ?? `${apiBook.id}-1`),
          isbn: primaryEdition?.isbn ? String(primaryEdition.isbn) : "",
          sankhyaCode: primaryEdition?.erp_code ? String(primaryEdition.erp_code) : "",
          materialidadeId: apiBook.materiality?.id ?? 0,
          materialidade:
            apiBook.materiality?.name ??
            MATERIALITIES.find((m) => m.id === apiBook.materiality?.id)?.name ??
            "",
          primaryCategory: primaryCodes.join(", "),
          secondaryCategory: secondaryCodes.join(", "),
          categoryConfigs,
          cost: Number(apiBook.cost ?? 0),
          contractType,
          contractExpiryDate: apiBook.contract_expiration ?? "",
          contractStatus: contractSituationFromApi(
            apiBook.contract_type,
            apiBook.contract_expiration,
          ),
          bookStatus: apiBook.status === "active" ? "ATIVO" : "INATIVO",
          simulationStatus: mapSimulationStatus(apiBook.eligible_for_simulation),
          lastUpdate: "agora",
        }
      : id
        ? mockBookDetails[id]
        : null;

  const book = resolvedBook;
  const [simulationStatus, setSimulationStatus] = useState<BookDetails["simulationStatus"]>(
    book?.simulationStatus || "INAPTO",
  );

  useEffect(() => {
    if (resolvedBook?.simulationStatus != null) {
      setSimulationStatus(resolvedBook.simulationStatus);
    }
  }, [resolvedBook?.id, resolvedBook?.simulationStatus]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen bg-white">
        <Navigation />
        <div className="flex-1 p-8">
          <p className="text-[#3B494A]">Carregando livro...</p>
        </div>
      </div>
    );
  }

  if (isError || !book) {
    return (
      <div className="flex min-h-screen bg-white">
        <Navigation />
        <div className="flex-1 p-8">
          <p className="text-[#3B494A]">Livro não encontrado</p>
        </div>
      </div>
    );
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Navigation />

      <div className="flex-1 flex flex-col">
        {/* Header with back button */}
        <div className="flex items-center h-[69px] px-6 border-b border-[#BAC9CA]">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/catalog")}
            className="w-12 h-12 rounded-full hover:bg-[#0000000D]"
          >
            <ArrowLeft className="w-6 h-6 text-[#151C1D]" />
          </Button>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-auto">
          <div className="px-8 py-9 w-full">
            {/* Book header */}
            <div className="flex justify-between items-start mb-9 gap-4">
              <div className="flex-1">
                <div className="flex flex-col gap-3 mb-4">
                  <h1 className="text-2xl font-medium text-[#151C1D]">
                    {book.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-[#3B494A]">
                        Status do livro
                      </span>
                      <Badge
                        className={`h-6 px-2 ${getBookStatusBadgeClass(book.bookStatus)} font-bold`}
                      >
                        {book.bookStatus}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-[#3B494A]">
                        Apto para simulação
                      </span>
                      <Badge
                        className={`h-6 px-2 gap-1 ${getSimulationBadgeClass(simulationStatus)} font-bold`}
                      >
                        {simulationStatus === "APTO" && (
                          <PlayCircle className="w-[15px] h-[15px]" />
                        )}
                        {simulationStatus}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 text-right">
                <div className="text-xs text-[#3B494A]">
                  Última atualização {book.lastUpdate}
                </div>
                <div className="w-px h-10 bg-[#BAC9CA]"></div>
                <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-10 h-10 rounded-lg hover:bg-[#0000000D]"
                    >
                      <MoreVertical className="w-5 h-5 text-[#3B494A]" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[220px]">
                    <DropdownMenuItem 
                      className="h-9 gap-2"
                      onClick={() => navigate(`/catalog/${book.id}/editar`)}
                    >
                      <Edit className="w-4 h-4 text-[#3B494A]" />
                      <span className="text-sm">Editar livro</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="h-9 gap-2"
                      disabled={toggleStatusMutation.isPending}
                      onClick={async () => {
                        await toggleStatusMutation.mutateAsync(Number(book.id));
                        setIsMenuOpen(false);
                      }}
                    >
                      <XCircle className="w-4 h-4 text-[#3B494A]" />
                      <span className="text-sm">Alternar status</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="h-9 gap-2"
                      disabled={simulationEligibilityMutation.isPending}
                      onClick={async () => {
                        const nextEligible = simulationStatus !== "APTO";
                        await simulationEligibilityMutation.mutateAsync({
                          id: Number(book.id),
                          body: { simulation_eligible: nextEligible },
                        });
                        setSimulationStatus(nextEligible ? "APTO" : "INAPTO");
                        setIsMenuOpen(false);
                      }}
                    >
                      <PlayCircle className="w-4 h-4 text-[#3B494A]" />
                      <span className="text-sm">
                        {simulationStatus === "APTO"
                          ? "Remover da simulação"
                          : "Apto para simulação"}
                      </span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Content sections - REORDERED: ERP/ISBN, Identificação, Categorias */}
            <div className="flex flex-col gap-5">
              {/* ERP/ISBN - FIRST */}
              <div className="rounded-3xl bg-[#F3FAFB] p-4">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-medium text-[#151C1D]">
                      ERP/ISBN
                    </h2>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-10 h-10 rounded-lg"
                    onClick={() => navigate(`/catalog/${book.id}/editar?section=versoes`)}
                  >
                    <Edit className="w-5 h-5 text-[#3B494A]" />
                  </Button>
                </div>
                <div className="border-t border-[#BAC9CA]"></div>
                <div className="pt-4 space-y-2">
                  {/* Sankhya Code */}
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-xs font-medium text-[#3B494A]">
                        Código Sankhya
                      </div>
                      <div className="text-base text-[#151C1D] mt-1">
                        {book.sankhyaCode}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(book.sankhyaCode)}
                      className="w-5 h-5 text-[#3B494A]"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  {/* ISBN */}
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-xs font-medium text-[#3B494A]">
                        ISBN
                      </div>
                      <div className="text-base text-[#151C1D] mt-1">
                        {book.isbn}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(book.isbn)}
                      className="w-5 h-5 text-[#3B494A]"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Identificação do Livro - SECOND */}
              <div className="rounded-3xl bg-[#F3FAFB] p-4">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-medium text-[#151C1D]">
                      Identificação do Livro
                    </h2>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-10 h-10 rounded-lg"
                    onClick={() => navigate(`/catalog/${book.id}/editar?section=identificacao`)}
                  >
                    <Edit className="w-5 h-5 text-[#3B494A]" />
                  </Button>
                </div>
                <div className="border-t border-[#BAC9CA]"></div>
                <div className="pt-4 space-y-2">
                  {/* Title */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A]">
                      Título
                    </div>
                    <div className="text-base text-[#151C1D] mt-1">{book.title}</div>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  {/* Cost */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A]">
                      Custo
                    </div>
                    <div className="text-base text-[#151C1D] mt-1">
                      R$ {book.cost.toFixed(2).replace(".", ",")}
                    </div>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  {/* Materiality */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A]">
                      Materialidade
                    </div>
                    <div className="text-base text-[#151C1D] mt-1">
                      {book.materialidade}
                    </div>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  {/* Status do livro (ativo / inativo) */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A]">
                      Status do livro
                    </div>
                    <div className="text-base text-[#151C1D] mt-1">
                      <Badge
                        className={`h-6 px-2 ${getBookStatusBadgeClass(book.bookStatus)} font-bold`}
                      >
                        {book.bookStatus}
                      </Badge>
                    </div>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  {/* Apto para simulação */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A]">
                      Apto para simulação
                    </div>
                    <div className="text-base text-[#151C1D] mt-1">
                      <Badge
                        className={`h-6 px-2 gap-1 ${getSimulationBadgeClass(simulationStatus)} font-bold`}
                      >
                        {simulationStatus === "APTO" && (
                          <PlayCircle className="w-[15px] h-[15px]" />
                        )}
                        {simulationStatus}
                      </Badge>
                    </div>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  {/* Contract Expiration */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A]">
                      Expiração do Contrato
                    </div>
                    <div className="text-base text-[#151C1D] mt-1">
                      {book.contractExpiryDate || "—"}
                    </div>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  {/* Contract Type */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A]">
                      Tipo de Contrato
                    </div>
                    <div className="text-base text-[#151C1D] mt-1">
                      {book.contractType || "—"}
                    </div>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A]">
                      Situação do contrato
                    </div>
                    <div className="text-base text-[#151C1D] mt-1">
                      {book.contractStatus || "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Categorias - THIRD */}
              <div className="rounded-3xl bg-[#F3FAFB] p-4">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-medium text-[#151C1D]">
                      Categorias e CSAT
                    </h2>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-10 h-10 rounded-lg"
                    onClick={() => navigate(`/catalog/${book.id}/editar?section=categorias`)}
                  >
                    <Edit className="w-5 h-5 text-[#3B494A]" />
                  </Button>
                </div>
                <div className="border-t border-[#BAC9CA]"></div>
                <div className="pt-4 space-y-2">
                  {/* Primary Category */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A] mb-2">
                      Categoria Primária
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {splitCats(book.primaryCategory).map((cat) => {
                        const config = book.categoryConfigs[cat] || { csat: "", withoutNote: false };
                        return (
                          <div key={cat} className="flex flex-col gap-1">
                            <Badge className="h-6 px-2 rounded bg-[#D6E5E6] text-[#3B494A] text-xs font-semibold">
                              {cat}
                            </Badge>
                            <div className="text-xs text-[#3B494A]">
                              CSAT: {config.withoutNote ? "—" : (config.csat || "—")}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="border-t border-[#BAC9CA]"></div>
                  {/* Secondary Category */}
                  <div className="py-3">
                    <div className="text-xs font-medium text-[#3B494A] mb-2">
                      Categoria Secundária
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {splitCats(book.primaryCategory).length === 0 &&
                      splitCats(book.secondaryCategory).length === 0 ? (
                        <span className="text-sm text-[#3B494A]">-</span>
                      ) : null}
                      {/* Show all primary categories first (auto-included) */}
                      {splitCats(book.primaryCategory).map((cat) => {
                        const config = book.categoryConfigs[cat] || { csat: "", withoutNote: false };
                        return (
                          <div key={`primary-${cat}`} className="flex flex-col gap-1">
                            <Badge className="h-6 px-2 rounded bg-[#EDF4F5] text-[#3B494A] text-xs font-semibold">
                              {cat}
                            </Badge>
                            <div className="text-xs text-[#3B494A]">
                              CSAT: {config.withoutNote ? "—" : (config.csat || "—")}
                            </div>
                          </div>
                        );
                      })}
                      {/* Then show additional secondary categories (not in primary) */}
                      {splitCats(book.secondaryCategory).filter(cat => !splitCats(book.primaryCategory).includes(cat)).map((cat) => {
                        const config = book.categoryConfigs[cat] || { csat: "", withoutNote: false };
                        return (
                          <div key={`secondary-${cat}`} className="flex flex-col gap-1">
                            <Badge className="h-6 px-2 rounded bg-[#EDF4F5] text-[#3B494A] text-xs font-semibold">
                              {cat}
                            </Badge>
                            <div className="text-xs text-[#3B494A]">
                              CSAT: {config.withoutNote ? "—" : (config.csat || "—")}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
